import { getApiErrorBody } from "./StringUtils";
// "YYYY-MM" is not a payroll idea — bills use it for the VAT credit period too
// — so the helpers live with the other calendar ones and are re-exported here
// for the payroll screens that already import them from this module.
export { toPeriod, fromPeriod, isPeriod } from "./calendarDate";

// Why the payroll Lambda left an employee out of a run. Every code it can send
// has a sentence; an unknown one falls back to the generic line rather than
// reaching the screen raw.
export const SKIP_REASON_KEYS = {
  UNSUPPORTED_EMPLOYEE_TYPE: "payroll.skipReasons.unsupportedEmployeeType",
  UNSUPPORTED_TAX_METHOD: "payroll.skipReasons.unsupportedTaxMethod",
  NO_BASE_SALARY: "payroll.skipReasons.noBaseSalary",
  EMPLOYEE_INACTIVE: "payroll.skipReasons.employeeInactive",
  COVERED_BY_OPENING_BALANCE: "payroll.skipReasons.coveredByOpeningBalance",
};

export const getSkipReasonKey = (reason) =>
  SKIP_REASON_KEYS[reason] || "payroll.skipReasons.other";

/**
 * The employees a 422 "nobody to pay" response lists, or null for any other
 * error. They come back under `error.skipped`, which getErrorMessages would
 * otherwise flatten into "[object Object]" lines.
 */
export const getSkippedFromError = (error) => {
  const body = getApiErrorBody(error);
  const skipped = body?.error?.skipped;
  return Array.isArray(skipped) ? skipped : null;
};

/** The message of an error whose body also carries structured data. */
export const getErrorMessage = (error) => getApiErrorBody(error)?.message || null;

/**
 * The account codes a 422 "cannot post" names as missing from the chart, or
 * null for any other error.
 */
export const getMissingAccountCodes = (error) => {
  const codes = getApiErrorBody(error)?.error?.missingAccountCodes;
  return Array.isArray(codes) && codes.length ? codes : null;
};

/** Journals the payroll Lambda booked carry their run; they change only there. */
export const isPayrollJournal = (transaction) => Boolean(transaction?.payrollRunID);

/** "2026-09" for a payroll journal (referenceNumber PAYROLL-2026-09), else null. */
export const getPayrollJournalPeriod = (transaction) => {
  const match = /^PAYROLL-(\d{4}-(0[1-9]|1[0-2]))$/.exec(transaction?.referenceNumber || "");
  return match ? match[1] : null;
};

export const sortPayrollItems = (items = []) =>
  [...items].sort(
    (a, b) =>
      (a.employeeName || "").localeCompare(b.employeeName || "", "id") ||
      (a.employeeID || "").localeCompare(b.employeeID || ""),
  );

// Biaya jabatan: 5% of gross, at most Rp500.000 a month (PMK 168/2023). The
// server enforces the same two caps on an opening balance.
export const POSITION_COST_RATE = 0.05;
export const POSITION_COST_MONTHLY_MAX = 500000;

export const getMaxPositionCost = (grossIncome, months) =>
  Math.min(
    Math.ceil((Number(grossIncome) || 0) * POSITION_COST_RATE),
    POSITION_COST_MONTHLY_MAX * (Number(months) || 0),
  );


// The withholdings a posted run owes, paid apart. `prefix` names the run
// fields that record the payment (pph21PaidAt, pph21TransactionID, ...);
// `dueDay` and `dueNextMonth` place the legal deadline: PPh 21 on the 15th of
// the next month (PMK 81/2024), BPJS Ketenagakerjaan on the 15th of the next
// month, BPJS Kesehatan on the 10th of the month itself.
export const REMITTANCES = [
  { kind: "PPH21", prefix: "pph21", dueDay: 15, dueNextMonth: true },
  { kind: "BPJS_HEALTH", prefix: "bpjsHealth", dueDay: 10, dueNextMonth: false },
  { kind: "BPJS_EMPLOYMENT", prefix: "bpjsEmployment", dueDay: 15, dueNextMonth: true },
];

const sumOf = (items, pick) => items.reduce((total, item) => total + (pick(item) || 0), 0);
const deducted = (items, types) =>
  sumOf(items, (item) =>
    (item.deductions || [])
      .filter((line) => types.includes(line.type))
      .reduce((total, line) => total + line.amount, 0),
  );

/**
 * What each withholding comes to, by the server's rule (payroll_journal.py):
 * PPh 21 is everything posting credited to Hutang PPh 21; the two BPJS amounts
 * add up to what it credited to Hutang BPJS. Shown before paying - the server
 * computes the amount it books itself.
 */
export const getRemittanceAmounts = (items = []) => ({
  PPH21: sumOf(items, (item) => item.pph21) + deducted(items, ["PPH21"]),
  BPJS_HEALTH: sumOf(items, (item) => item.employerHealth) + deducted(items, ["BPJS_KESEHATAN"]),
  BPJS_EMPLOYMENT:
    sumOf(
      items,
      (item) => item.employerJkk + item.employerJkm + item.employerJht + item.employerJp,
    ) +
    deducted(items, [
      "BPJS_KETENAGAKERJAAN_JHT",
      "BPJS_KETENAGAKERJAAN_JP",
      "BPJS_KETENAGAKERJAAN_JKP",
    ]),
});

/** The day a withholding for a "YYYY-MM" payroll is due, local midnight. */
export const getRemittanceDueDate = (remittance, period) => {
  const [year, month] = period.split("-").map(Number);
  return new Date(year, month - 1 + (remittance.dueNextMonth ? 1 : 0), remittance.dueDay);
};

/** The first day of a "YYYY-MM" month, local midnight. */
export const periodStart = (period) => {
  const [year, month] = period.split("-").map(Number);
  return new Date(year, month - 1, 1);
};

/** The last day of a "YYYY-MM" month, local midnight - where posting dates the accrual. */
export const periodEnd = (period) => {
  const [year, month] = period.split("-").map(Number);
  return new Date(year, month, 0);
};

/**
 * The code a payroll error carries at the top level of its body -
 * PREMIUM_REQUIRED, PERIOD_CLOSED - or null.
 */
export const getPayrollErrorCode = (error) => getApiErrorBody(error)?.code || null;
